import { useEffect, useMemo, useState, type RefObject } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { BLOCK_DEFS } from '@docgen/blocks';
import { GripVertical, Plus } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../lib/utils';

/**
 * The floating gutter that appears at a hovered block's left edge — the
 * drag handle (⠿) and the insert (+) affordance — plus the insert menu.
 * One floating control, positioned from the block's rect: block layout is
 * never wrapped or disturbed.
 */

export interface HoverGutterProps {
  targetId: string | null;
  deskRef: RefObject<HTMLDivElement | null>;
  onLock: (locked: boolean) => void;
  onOpenInsert: (blockId: string, at: { top: number; left: number }) => void;
  hidden: boolean;
}

export function HoverGutter({ targetId, deskRef, onLock, onOpenInsert, hidden }: HoverGutterProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!targetId) {
      setRect(null);
      return;
    }
    const el = deskRef.current?.querySelector(`[data-block="${CSS.escape(targetId)}"]`);
    setRect(el ? el.getBoundingClientRect() : null);
  }, [targetId, deskRef]);

  const drag = useDraggable({
    id: `gutter:${targetId ?? 'none'}`,
    data: { kind: 'move-block', blockId: targetId ?? '' },
    disabled: !targetId,
  });

  if (!targetId || !rect || hidden) return null;

  return (
    <div
      ref={drag.setNodeRef}
      className="fixed z-40 flex items-center gap-0.5 rounded-md border bg-card p-0.5 shadow-md"
      style={{ top: rect.top - 4, left: Math.max(rect.left - 58, 8) }}
      onMouseEnter={() => onLock(true)}
      onMouseLeave={() => onLock(false)}
    >
      <button
        type="button"
        aria-label="Insert block after"
        className="flex size-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-primary"
        onClick={() => onOpenInsert(targetId, { top: rect.top + 10, left: rect.left - 46 })}
      >
        <Plus className="size-4" />
      </button>
      <button
        type="button"
        aria-label="Drag to move block"
        className="flex size-6 cursor-grab items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
        {...drag.attributes}
        {...drag.listeners}
      >
        <GripVertical className="size-4" />
      </button>
    </div>
  );
}

export interface InsertMenuProps {
  at: { top: number; left: number };
  onPick: (blockType: string) => void;
  onClose: () => void;
}

export function InsertMenu({ at, onPick, onClose }: InsertMenuProps) {
  const [query, setQuery] = useState('');
  const defs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return BLOCK_DEFS;
    return BLOCK_DEFS.filter(
      (d) => d.name.toLowerCase().includes(q) || d.description.toLowerCase().includes(q),
    );
  }, [query]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && defs[0]) onPick(defs[0].type);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [defs, onClose, onPick]);

  const top = Math.min(at.top, window.innerHeight - 340);

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="fixed z-50 w-56 rounded-lg border bg-popover p-1.5 shadow-lg"
        style={{ top, left: Math.max(at.left, 8) }}
      >
        <Input
          autoFocus
          value={query}
          placeholder="Insert a block…"
          className="mb-1.5 h-7 text-xs"
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="max-h-64 overflow-y-auto">
          {defs.map((d) => (
            <button
              key={d.type}
              type="button"
              className={cn(
                'flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent',
              )}
              onClick={() => onPick(d.type)}
            >
              <span className="text-xs font-medium">{d.name}</span>
              <span className="ml-auto text-[9px] uppercase text-muted-foreground">{d.category}</span>
            </button>
          ))}
          {defs.length === 0 && (
            <p className="px-2 py-3 text-center text-[11px] text-muted-foreground">No matching blocks.</p>
          )}
        </div>
      </div>
    </>
  );
}
